import React, { useState, useEffect, useRef } from "react";
import { Row, Container, Col, Form, Button, Modal, Table, Alert } from "react-bootstrap";

import Crypt from '../lib/Crypt'
import Constants from '../lib/Constants'
import ErrorAlert from './ErrorAlert';
import swal from 'sweetalert';
import C from '../lib/Config';
import { getExtractionResult, ExtractionOutcome, ReportType } from '../lib/ClinicalExtraction';
import { mapPathologyResultToRows } from '../lib/PathologyExtractionMapper';
import './NewReport.css';

function passwordShow() {
    var x = document.getElementById("notRequiredFlds");
    if (!x) return;
    if (x.style.display === "none") {
        x.style.display = "";
    } else {
        x.style.display = "none";
    }
}
function toggleButton() {
    var buttonShow = document.getElementById("toggleShow");
    var buttonHide = document.getElementById("toggleHide");
    if (buttonShow.style.display === "none") {
        buttonShow.style.display = "block";
        buttonHide.style.display = "none";
    } else {
        buttonShow.style.display = "none";
        buttonHide.style.display = "block";
    }
}

const NewReport = (props) => {
    const { show, closeModal, appointmentId, mode, data, reportSavedSuccess, protocolId, patientId } = props;
    const [reportList, setReportList] = useState([]);
    const [reportLabel, setReportLabel] = useState(null);
    const [reportId, setReportId] = useState(null);
    const [reportDataList, setReportDataList] = useState([{}]);
    const [dataTypeValidationMsg, setDataTypeValidationMsg] = useState({});
    const [successAlertshow, setSuccessAlertshow] = useState(false);
    const [errorAlertshow, errorAlertclose] = useState(false);
    const [disable, setDisable] = useState(true);
    const [showText, setShowText] = useState(false);
    const [reportErrors, setReportErrors] = useState({});
    // Latest PATHOLOGY extraction for this patient, read once when the modal
    // opens. Held in a ref as well as state because blankRportByIdApiCall runs
    // from a fetch callback and would otherwise close over a stale value.
    const [extractionNotice, setExtractionNotice] = useState(null);
    const extractionResultRef = useRef(null);
    // Bumped whenever a fresh set of rows is loaded. The result inputs are
    // uncontrolled (defaultValue), so they only pick up a pre-filled value when
    // React builds a new element — changing the key is what forces that.
    const [rowsVersion, setRowsVersion] = useState(0);

        // Function to validate required fields before saving
        const validateResults = () => {
            let errors = {};
            let allRequiredFieldsFilled = true; // Flag to track if all required fields are filled
    
            reportDataList.forEach(item => {
                if (item.required === 'Y' && (!item.result || item.result.trim() === '')) {
                    errors[item.testName] = 'This field is required'; // Set error message for empty required fields
                    allRequiredFieldsFilled = false; // Mark the form as invalid if any required field is empty
                }
            });
    
            setReportErrors(errors);
            return allRequiredFieldsFilled; // Return true if all required fields are filled, false otherwise
        };

    useEffect(() => {
        switch (mode) {
            case Constants.MODE_NEW_REPORT:
                reportListApiCall();
                fetchPathologyExtraction();
                break;

            case Constants.MODE_VIEW_REPORT:
                getAppointmentReport(data.apptXReportId);
                break;

            default:
                break;
        }
    }, [mode])

    // Read the latest pathology extraction for this patient as soon as the
    // modal opens. It is a plain read — it never starts a scan; scanning is
    // triggered from Case Summary.
    //
    // Nothing is filled at this point: the form has no parameter rows until a
    // report type is chosen. The result is parked here and applied by
    // blankRportByIdApiCall once those rows exist.
    const fetchPathologyExtraction = async () => {
        extractionResultRef.current = null;
        setExtractionNotice(null);

        if (!patientId) return;

        const res = await getExtractionResult(patientId, ReportType.PATHOLOGY);

        if (res.outcome === ExtractionOutcome.UNAUTHORIZED) {
            setExtractionNotice({
                variant: 'warning',
                text: 'Your session has expired, so the scanned report could not be read. Please sign in again.',
            });
            return;
        }

        const isSuccess =
            res.outcome === ExtractionOutcome.SUCCESS ||
            res.outcome === ExtractionOutcome.SUCCESS_STALE;

        if (!isSuccess) {
            // "Nothing scanned yet" is the ordinary case here, not an error —
            // the form stays perfectly usable, so say nothing at all.
            return;
        }

        const result = (res.data && res.data.result) || null;
        extractionResultRef.current = result;

        if (result && res.outcome === ExtractionOutcome.SUCCESS_STALE) {
            setExtractionNotice({
                variant: 'warning',
                text: 'Showing values from an earlier scan — the most recent scan of this patient’s reports did not succeed.',
            });
        }
    }

    // Fills the freshly loaded rows from the parked extraction, if there is one.
    const applyExtractionToRows = (dataList) => {
        const result = extractionResultRef.current;
        if (!result) return dataList;

        const { rows, filledCount, unmatched, unitMismatched } =
            mapPathologyResultToRows(result, dataList);

        if (filledCount === 0 && unitMismatched.length === 0) {
            // The scan found nothing belonging on this particular report.
            // Silent: the user may simply have picked an unrelated report type.
            return dataList;
        }

        const parts = [];
        if (filledCount > 0) {
            parts.push(
                `${filledCount} value${filledCount > 1 ? 's' : ''} filled from the scanned report — please verify before saving.`
            );
        }
        if (unitMismatched.length > 0) {
            parts.push(
                `Left blank because the report used a different unit: ${unitMismatched.join(', ')}.`
            );
        }
        if (unmatched.length > 0) {
            parts.push(`Not on this report: ${unmatched.join(', ')}.`);
        }

        setExtractionNotice({
            variant: filledCount > 0 ? 'info' : 'warning',
            text: parts.join(' '),
        });

        return rows;
    }

    const reportListApiCall = async () => {
        fetch(C.getUrl() + '/reportMst/findAll/')
            .then(res => res.json())
            .then(res => {
                //console.log('--> REPORT MASTER STATUS : ' + res.status)
                let data = JSON.parse(res.message);
                //console.log('--> REPORT MASTER - MESSAGE DATA: ' + JSON.stringify(data));
                if (data !== null) {
                    let reportData = [{ key: Crypt.getUniqueKey('0'), id: 'DEFAULT', label: '- Select Report -' }];
                    data.map((item, index) => {
                        let jsonList = new Object();
                        jsonList['key'] = Crypt.getUniqueKey(index);
                        jsonList['id'] = item.reportId + '';
                        jsonList['label'] = item.reportName;
                        reportData.push(jsonList);
                    });
                    //console.table(reportData)
                    setReportList(reportData)
                }

            })
            .catch(err => {
                swal({ title: "Warning!", text: "Error : " + err, icon: "error", buttons: false, timer: 2000, });
            });
    }

    const blankRportByIdApiCall = async (id, patientId) => {
        fetch(C.getUrl() + `/reportParamMst/fetchBlankReportByReportId/${id}/${patientId}`)
            .then(res => res.json())
            .then(res => {
                //console.log('--> BLANK REPORT MASTER STATUS : ' + res.status)
                let data = JSON.parse(res.message);
                //console.log('-->  BLANK REPORT MASTER - MESSAGE DATA: ' + JSON.stringify(data));
                setReportDataList([{}]);
                if (data !== null) {
                    let dataList = [];
                    data.map((item, index) => {
                        let jsonList = new Object();
                        jsonList['key'] = Crypt.getUniqueKey(index);
                        jsonList['id'] = item.reportParamId + '';
                        jsonList['testName'] = item.reportParamName;
                        jsonList['result'] = '';
                        jsonList['unit'] = item.unitMstDTO.unitName;
                        jsonList['referenceRange'] = item.normalRange;
                        jsonList['dataType'] = item.dataType;
                        jsonList['required'] = item.required;
                        dataList = [...dataList, jsonList];
                    });
                    //console.table(dataList)
                    // Pre-populate from the scanned pathology report before the
                    // rows are handed to React, so the uncontrolled inputs mount
                    // already carrying their defaultValue.
                    setReportDataList(applyExtractionToRows(dataList))
                    setRowsVersion((v) => v + 1)
                }

            })
            .catch(err => {
                swal({ title: "Warning!", text: "Error : " + err, icon: "error", buttons: false, timer: 2000, });
            });
    }

    const getAppointmentReport = async (id) => {
        fetch(C.getUrl() + `/followupAppt/appointmentReport/${id}`)
            .then(res => res.json())
            .then(res => {
                //console.log('--> APPOINTMENT REPORT STATUS : ' + res.status)
                let data = JSON.parse(res.message);
                //console.log('-->  APPOINTMENT REPORT - MESSAGE DATA: ' + JSON.stringify(data));
                let reportValuesDataList = data.reportValues;
                //console.log('-->  APPOINTMENT REPORT - REPORT VALUES DATA: ' + JSON.stringify(reportValuesDataList));
                setReportDataList([{}]);
                if (data !== null) {
                    let dataList = [];
                    reportValuesDataList.map((item, index) => {
                        let jsonList = new Object();
                        jsonList['key'] = Crypt.getUniqueKey(index);
                        jsonList['id'] = item.reportParameterMstDTO.reportParamId + '';
                        jsonList['testName'] = item.reportParameterMstDTO.reportParamName;
                        jsonList['result'] = item.value;
                        jsonList['unit'] = item.reportParameterMstDTO.unitMstDTO.unitName;
                        jsonList['referenceRange'] = item.normalRange;
                        jsonList['dataType'] = '';
                        dataList = [...dataList, jsonList];
                    });
                    //console.table(dataList)
                    setReportDataList(dataList)
                }

            })
            .catch(err => {
                swal({ title: "Warning!", text: "Error : " + err, icon: "error", buttons: false, timer: 2000, });
            });
    }

    const saveReport = async () => {
        if (!validateResults()) { // Add this validation check
            swal({
                title: "Warning!",
                text: "Please fill out all required fields before submitting the report.",
                icon: "warning",
                buttons: false,
                timer: 2000,
            });
            return; // Stop the save process if validation fails
        }
        let data = new Object();
            //Report data JSON object
            let reportData = new Object();
            reportList.map((item, i) => {
                if (item.id === reportId) {
                    reportData['reportId'] = item.id;
                    reportData['reportName'] = item.label;
                }
            })

            //Follow up appointment data JSON object
            let followUpApptData = new Object();
            followUpApptData['appointmentId'] = appointmentId;

            //Created by user id JSON data
            let userIdData = new Object();
            userIdData['userId'] = localStorage.getItem('userId');

            //Report values data JSON Array
            let reportValuesArray = [];
            reportDataList.map((item, i) => {
                if(item.result != '' && item.result != undefined){
                    let singleItem = new Object();
                    let data = new Object();
                    data['reportParamId'] = item.id;
                    data['reportParamName'] = item.testName;
                    singleItem['reportValId'] = '';
                    singleItem['normalRange'] = item.referenceRange;
                    singleItem['value'] = item.result;
                    singleItem['reportParameterMstDTO'] = data;
                    singleItem['createdByIdUser'] = userIdData;
                    singleItem['updatedByIdUser'] = userIdData;
                    singleItem['createdDt'] = new Date();
                    singleItem['updatedDt'] = new Date();
                    reportValuesArray = [...reportValuesArray, singleItem];
                }
            })

            data['apptXReportId'] = '';
            data['reportMasterDTO'] = reportData
            data['followupAppointmentDTO'] = followUpApptData;
            data['createdByIdUser'] = userIdData;
            data['updatedByIdUser'] = userIdData;
            data['createdDt'] = new Date();
            data['updatedDt'] = new Date();
            data['reportValues'] = reportValuesArray;
            // console.log('---> SAVE REPORT JSON DATA : ' + JSON.stringify(data));
            return fetch(C.getUrl() + '/followupAppt/saveReport', {
                method: 'POST',
                mode: 'cors',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(res => res.json())
            .then(res => {
                let obj = res;
                let error = obj.subErrors;
                if (error !== null && error !== "") {
                    alert(error);
                } else {
                    reportSavedSuccessHandler();
                }
            })
            .catch(err => {
                //console.log("Error : " + err);
                swal({ title: "Warning!", text: "Error : " + err, icon: "error", buttons: false, timer: 2000, });
            });
        }

    const isReportExist = async (appointmentId, reportId, patientId) => {
        let isReportExistdata ={
            followupAppointmentDTO : {
                appointmentId : appointmentId
            },
            reportMasterDTO : {
                reportId : reportId
            }
        }
        return fetch(C.getUrl() + '/followupAppt/isReportExist', {
            method: 'POST',
            mode: 'cors',
            body: JSON.stringify(isReportExistdata),
            headers: {
                'Content-Type': 'application/json'
            }
        })
        .then(res => res.json())
        .then(res => {
            let obj = res;
            if (obj !== null && obj !== "undefined" && obj==true) {
                setReportDataList([{}]);
                swal({ title: "Warning!", text: "Report already exists.", icon: "error", buttons: false, timer: 2000, });
            } else if(obj==false) {
                blankRportByIdApiCall(reportId, patientId);
            }
        })
        .catch(err => {
            console.log("Error : " + err);
            errorAlertshow(true);
            setTimeout(() => {
                errorAlertshow(false);
            }, 3000)
            swal({ title: "Warning!", text: "Error : " + err, icon: "error", buttons: false, timer: 2000, });
        });
    }

    const resetDataAndCloseModal = () => {
        setReportId(null);
        setReportDataList([{}])
        setExtractionNotice(null);
        extractionResultRef.current = null;
        closeModal();
    }

    const reportSavedSuccessHandler = () => {
        setReportId(null);
        setReportDataList([{}]);
        setExtractionNotice(null);
        extractionResultRef.current = null;
        reportSavedSuccess();
    }

    const validateForm = (event) => {
        let errors = {};
        //console.log('-- UPDATED LIST DATA :: ' + JSON.stringify(reportDataList));
        reportDataList.map((item, i) => {
            if (event.target.name === item.testName) {
                switch (item.dataType) {
                    case 'A': //“A” (Alphabet) then it can accept alphabets plus special signs “.” and “-”
                        if (!isValidDataType('A', event)) {
                            reportDataList[i].result = '';
                            document.getElementById(item.testName).value = '';
                            errors[item.testName] = 'Please enter alphabets only'
                            setDataTypeValidationMsg(errors);
                        } else {
                            errors[item.testName] = ''
                            setDataTypeValidationMsg(errors);
                            setDisable(false);
                        }
                        break;

                    case 'N': //“N” (Numeric) then check if it is valid number greater than or equal to zero. Can be a number with decimal point (float)
                        if (!isValidDataType('N', event)) {
                            reportDataList[i].result = '';
                            document.getElementById(item.testName).value = '';
                            errors[item.testName] = 'Please enter valid number.'
                            setDataTypeValidationMsg(errors);
                        } else {
                            errors[item.testName] = ''
                            setDataTypeValidationMsg(errors);
                            setDisable(false);
                        }
                        break;

                    case 'M': //“M” (Mixed) then it can be alphanumeric plus special characters “.” and “-”
                        if (!isValidDataType('M', event)) {
                            reportDataList[i].result = '';
                            document.getElementById(item.testName).value = '';
                            errors[item.testName] = `Please avoid special characters other than ${'(.)'} and ${'(-)'}`
                            setDataTypeValidationMsg(errors);
                        } else {
                            errors[item.testName] = ''
                            setDataTypeValidationMsg(errors);
                            setDisable(false);
                        }
                        break;

                    default:
                        reportDataList[i].result = '';
                        document.getElementById(item.testName).value = '';
                        errors[item.testName] = ''
                        setDataTypeValidationMsg(errors);
                        break;
                }
            }
        })
    }

    const isValidDataType = (dataType, event) => {
        let alphabetPattern = /^[a-zA-Z.-]+$/;
        let alphabetNumericPattern = /^[a-zA-Z0-9.-]+$/;
        let val = event.target.value;
        let num = parseFloat(val, 10);
        switch (dataType) {
            case 'A':
                if (alphabetPattern.test(val)) {
                    return true;
                } else {
                    return false;
                }

            case 'N':
                if (num >= 0) {
                    return true;
                } else {
                    return false;
                }

            case 'M':
                if (num >= 0 || alphabetNumericPattern.test(val)) {
                    return true;
                } else {
                    return false;
                }

            default:
                break;
        }
    }

    // Styling for an input the scan pre-filled, so a clinician can tell an
    // extracted value from one they typed. `raw_text` goes in the tooltip as
    // the audit trail back to the source document.
    const extractionFieldProps = (item) => (
        item.extractionFilled
            ? {
                className: 'extraction-filled',
                title: item.extractionRawText
                    ? `From the scanned report: ${item.extractionRawText}`
                    : 'Filled from the scanned report',
            }
            : {}
    );

    const renderListItem = (item, index) => {
        if(item.required == 'Y' || item.required == 'y'){
            return (
                <React.Fragment key={`${rowsVersion}-${item.id || index}`}>
                    <tr>
                        <td>{item.testName}</td>
                        <td className="text-center">
                            <Form.Control
                                id={item.testName}
                                type='text'
                                placeholder=''
                                name={item.testName}
                                defaultValue={item.result ?? ''}
                                onBlur={(event) => validateForm(event)}
                                onChange={(event) => reportResultValuesHandler(event)}
                                isInvalid={!!reportErrors[item.testName]} // Bootstrap class to show error styling
                                {...extractionFieldProps(item)}
                            />

                            <div className="invalid-feedback">{dataTypeValidationMsg[item.testName]}</div>
                        </td>
                        <td className="text-center">{item.unit}</td>
                        <td className="text-center">{item.referenceRange}</td>
                    </tr>
                </React.Fragment>
            );
        }
    }

    const renderNonRequiredListItem = (item, index) => {
        if(item.required == 'N' || item.required == 'n'){
            return (
                <tr key={`${rowsVersion}-${item.id || index}`}>
                    <td>{item.testName}</td>
                    <td className="text-center">
                        <Form.Control
                            id={item.testName}
                            type='text'
                            placeholder=''
                            name={item.testName}
                            defaultValue={item.result ?? ''}
                            //onBlur={(event) => validateForm(event)}
                            onChange={(event) => reportResultValuesHandler(event)}
                            style={{width: '98%', margin: 'auto', marginLeft: '15px'}}
                            {...extractionFieldProps(item)}
                        />
                        <div className="invalid-feedback">{dataTypeValidationMsg[item.testName]}</div>
                    </td>
                    <td className="text-center">{item.unit}</td>
                    <td className="text-center">{item.referenceRange}</td>
                </tr>
            );
        }
    }

    const renderListItemForView = (item, index) => {
        return (
            <tr key={index}>
                <td>{item.testName}</td>
                <td className="text-center">
                    <Form.Control
                        id={item.testName}
                        type='text'
                        placeholder=''
                        name={item.testName}
                        value={item.result ?? ""}
                        disabled/>
                    <div className="invalid-feedback">{dataTypeValidationMsg[item.testName]}</div>
                </td>
                <td className="text-center">{item.unit}</td>
                <td className="text-center">{item.referenceRange}</td>
            </tr>
        );
    }

    const reportResultValuesHandler = (event) => {
        reportDataList.map((item, i) => {
            if (event.target.name === item.testName) {
                reportDataList[i].result = event.target.value;
            }
        })
    }

    const handleChange = (event) => {
        if (event.target.name === 'reportList') {
            setReportId(event.target.value);
            isReportExist(appointmentId, event.target.value, patientId);
        }
    }

    return (
        <Row>
            <Modal id="report" show={show} animation={true} centered scrollable={true} size="lg">
                <Modal.Header closeButton onClick={() => resetDataAndCloseModal()}>
                    <Modal.Title>
                        <h3 className="fs-18 header_title mb-0 text-black">
                            {
                                (mode === Constants.MODE_NEW_REPORT) ?
                                    `New Report` :
                                    (
                                        ((mode === Constants.MODE_VIEW_REPORT) && (data !== null || data !== 'undefined')) ?
                                            data.reportMasterDTO.reportName :
                                            ``
                                    )
                            }
                        </h3>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        {
                            (mode === Constants.MODE_NEW_REPORT) ?
                                <Row>
                                    <Col lg='12'>
                                        <Form.Group>
                                            <Form.Control as="select" id="" name='reportList' onChange={(event) => handleChange(event)}>
                                                {
                                                    reportList.map((item, i) => {
                                                        return (
                                                            <option key={item.key} value={item.id}>{`${item.label}`}</option>
                                                        );
                                                    })
                                                }
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                </Row> :
                            null
                        }
                        {
                            (extractionNotice && (mode === Constants.MODE_NEW_REPORT)) ?
                                <Row>
                                    <Col lg='12'>
                                        <Alert
                                            variant={extractionNotice.variant}
                                            dismissible
                                            onClose={() => setExtractionNotice(null)}
                                        >
                                            {extractionNotice.text}
                                        </Alert>
                                    </Col>
                                </Row> :
                            null
                        }
                        {
                            (reportId !== null && (mode === Constants.MODE_NEW_REPORT)) ?
                                <Row>
                                    <Col lg='12'>
                                        <Table hover striped responsive="sm">
                                            <thead className='thead-light'>
                                                <tr>
                                                    <th>Test Name</th>
                                                    <th className="text-center">Result</th>
                                                    <th className="text-center">Unit</th>
                                                    <th className="text-center">Reference Range</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    reportDataList.map((item, index) => {
                                                        return (
                                                            renderListItem(item, index)
                                                        );
                                                    })
                                                }
                                            </tbody>
                                            <tbody id="notRequiredFlds" style={{display: "none"}}>
                                                {
                                                    reportDataList.map((item, index) => {
                                                        return (
                                                            renderNonRequiredListItem(item, index)
                                                        );
                                                    })
                                                }
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row> :
                            (
                                (mode === Constants.MODE_VIEW_REPORT) ?
                                    <Row>
                                        <Col lg='12'>
                                            <Table hover responsive="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Test Name</th>
                                                        <th className="text-center">Result</th>
                                                        <th className="text-center">Unit</th>
                                                        <th className="text-center">Reference Range</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        reportDataList.map((item, index) => {
                                                            return (
                                                                renderListItemForView(item, index)
                                                            );
                                                        })
                                                    }
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row> :
                                null
                            )
                        }
                    </Container>
                </Modal.Body>
                <Modal.Footer className='text-center'>
                    <Button variant="outline-danger mr-2" onClick={() => resetDataAndCloseModal()}> Cancel </Button>
                    {(mode === Constants.MODE_NEW_REPORT) && (<>
                        <Button variant="outline-primary" id="toggleHide" title="Load More" onClick={() => {toggleButton(); passwordShow()}} >Load More</Button>
                        <Button variant="outline-primary" id="toggleShow" style={{display: "none"}} title="Show Less" onClick={() => {toggleButton(); passwordShow()}} >Show Less</Button>
                        <Button className="btn btn-secondary" onClick={() => saveReport()}> Save </Button>
                    </>)}
                </Modal.Footer>
            </Modal>
            {/* <ErrorAlert show={errorAlertshow} alertBody={'Something went wrong. Please try again'}/> */}
        </Row>
    )
}
export default NewReport;